import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 *
 * An individual structure in the target list. isTarget is the binary
 * target/off-target flag. source records how the entry was added (optional).
 * alignTransform/alignReferenceFrame are present only for an unloaded structure of
 * interest whose alignment was resolved at add time (raw USAlign transform + the
 * reference structureKey it targets); see redux/projectState/access.getTargetInfoForMapCase.
 * @typedef {{
 *   mapCase: MapCase, displayName: string, isTarget: boolean,
 *   source?: 'manual'|'query'|'curated',
 *   alignTransform?: { rotation: number[][], translation: number[] },
 *   alignReferenceFrame?: string,
 * }} IndividualTarget
 *
 * A named group of structures treated as a single target-list entry (e.g. a curated
 * kinase conformation panel). groupId is the stable key. Members are expected to be
 * unique by construction (curated panels come from per-structure lists); the store
 * does not dedup them. A group has NO target/off-target flag for now — its members are
 * scored only when pulled out as individuals. (A group-level flag, or a per-entry role,
 * can be added later.)
 * @typedef {{
 *   groupId: string, mapCases: MapCase[], displayName: string,
 *   source?: 'manual'|'query'|'curated',
 * }} GroupTarget
 *
 * @typedef {IndividualTarget | GroupTarget} TargetEntry
 * @typedef {TargetEntry[]} structList
 * @typedef {{
 *    proteinFamily: string,
 *    targetList: structList,
 *    scoringTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        scoring: Boolean,
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 *    compoundTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 * }} initialState
 */

const initialTableState = {
    scoringTable: {
        scoring: false,
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
        chartState: {
            coralMap: {
                activeCompounds: [],
                colorScheme: null,
                property: null,
                propertyThresholds: {},
                propertyPreferences: {},
            },
        },
    },
    compoundTable: {
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
    },
    screeningTable: {
        sidePanelWidth: 800,
        activeChart: 'bmapsTable',
        chartState: {
            coralMap: {
                activeProperty: null,
                propertySettings: {},
            },
        },
        /** @type {{smiles: string, baseHue: number, name: string, properties:{}}[]} */
        screeningCompoundInfoList: [],
    },
    jobsGridTable: {

    },
    // Right SidePanel column — added 2026-04-29 by
    // bmaps-sidepanel-co-display-and-resize study. Hosts HelpPanel
    // (chat), WorkflowPane, ResultsPane, KnowledgeDashboard. Persists
    // chosen width + minimize state across reloads.
    rightSidePanel: {
        // sidePanelWidth: 0 means "use default" until user resizes
        // (component computes 22rem from CSS at mount).
        sidePanelWidth: 0,
        minimized: false,
    },
};

/** @type {initialState} */
export const initialState = {
    proteinFamily: '',
    targetList: [],
    tableState: initialTableState,
};

const slice = createSlice({
    name: 'projectState',
    initialState,
    reducers: {
        addStructure(state, action) {
            // ensure that target id is unique.
            if (state.targetList.some((value) => value.mapCase === action.payload.mapCase)) {
                return ({ ...state });
            }
            return {
                ...state,
                targetList: [
                    ...state.targetList,
                    action.payload,
                ],
            };
        },
        changeTarget(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return ({
                        ...value,
                        isTarget: !value.isTarget,
                    });
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        setProteinTarget(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return (action.payload);
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        updateStructureAlignment(state, action) {
            const { mapCase, alignTransform, alignReferenceFrame } = action.payload;
            return {
                ...state,
                targetList: state.targetList.map((value) => (
                    value.mapCase === mapCase
                        ? { ...value, alignTransform, alignReferenceFrame }
                        : value
                )),
            };
        },
        setAllTargetInfo(state, action) {
            return {
                ...state,
                targetList: [...action.payload],
            };
        },
        removeStructure(state, action) {
            const newState = state.targetList.filter(
                (value) => value.mapCase !== action.payload.mapCase
            );
            return {
                ...state,
                targetList: [...newState],
            };
        },
        addGroup(state, action) {
            // ensure that group id is unique.
            if (state.targetList.some((value) => value.groupId === action.payload.groupId)) {
                return ({ ...state });
            }
            return {
                ...state,
                targetList: [...state.targetList, action.payload],
            };
        },
        removeGroup(state, action) {
            return {
                ...state,
                targetList: state.targetList.filter(
                    (value) => value.groupId !== action.payload.groupId
                ),
            };
        },
        changeProteinFamily(state, action) {
            return {
                ...state,
                proteinFamily: action.payload,
            };
        },
        setTableState(state, action) {
            state.tableState = { ...state.tableState, ...action.payload };
        },
        setCompoundTableState(state, action) {
            state.tableState.compoundTable = {
                ...state.tableState.compoundTable,
                ...action.payload,
            };
        },
        setScoringTableState(state, action) {
            state.tableState.scoringTable = {
                ...state.tableState.scoringTable,
                ...action.payload,
            };
        },
        setJobsCompoundsTableState(state, action) {
            state.tableState.jobsCompoundsTable = {
                ...state.tableState.jobsCompoundsTable,
                ...action.payload,
            };
        },
        setJobsGridTableState(state, action) {
            state.tableState.jobsGridTable = {
                ...state.tableState.jobsGridTable,
                ...action.payload,
            };
        },
        setScreeningTableState(state, action) {
            state.tableState.screeningTable = {
                ...state.tableState.screeningTable,
                ...action.payload,
            };
        },
        setTargetsTableState(state, action) {
            state.tableState.targetsTable = {
                ...state.tableState.targetsTable,
                ...action.payload,
            };
        },
        setCoralMapState(state, action) {
            state.tableState[action.payload.table].chartState.coralMap = {
                ...state.tableState[action.payload.table].chartState.coralMap,
                ...action.payload.coralMap,
            };
        },
        updateScreeningPropertySettings(state, action) {
            state.tableState.screeningTable.chartState.coralMap.propertySettings = {
                ...state.tableState.screeningTable.chartState.coralMap.propertySettings,
                ...action.payload,
            };
        },
        setActiveChart(state, action) {
            state.tableState[action.payload.table].activeChart = action.payload.chart;
        },
        setSidePanelWidth(state, action) {
            state.tableState[action.payload.table].sidePanelWidth = action.payload.sidePanelWidth;
        },
        setSidePanelMinimized(state, action) {
            state.tableState[action.payload.table].minimized = !!action.payload.minimized;
        },
        // Per-pane width / minimize setters introduced by
        // `bmaps-sidepanel-independent-columns` (2026-05-28). The legacy
        // setSidePanelWidth / setSidePanelMinimized above remain in place
        // for backward compatibility with non-stackable side panels
        // (CompoundTableSidePanel, ScoringTableSidePanel, etc.).
        setSidePanelPaneWidth(state, action) {
            const { table, paneId, width } = action.payload;
            if (!state.tableState[table]) state.tableState[table] = {};
            if (!state.tableState[table].panes) state.tableState[table].panes = {};
            if (!state.tableState[table].panes[paneId]) {
                state.tableState[table].panes[paneId] = { width: 0, minimized: false };
            }
            state.tableState[table].panes[paneId].width = width;
        },
        setSidePanelPaneMinimized(state, action) {
            const { table, paneId, minimized } = action.payload;
            if (!state.tableState[table]) state.tableState[table] = {};
            if (!state.tableState[table].panes) state.tableState[table].panes = {};
            if (!state.tableState[table].panes[paneId]) {
                state.tableState[table].panes[paneId] = { width: 0, minimized: false };
            }
            state.tableState[table].panes[paneId].minimized = !!minimized;
        },
        removeAllStructures(state) {
            return {
                ...state,
                targetList: [],
            };
        },
        reset() {
            return initialState;
        },
    },
});

export default slice;
